// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
// SPDX-License-Identifier: AGPL-3.0-or-later
//
// Real ABS SA4 boundaries plus every station as a point. Boundaries are
// downloaded by the pipeline from the ABS ArcGIS service and never hand-authored.

import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { loadBoundaries, loadSites, prefs } from '../data';
import type { Site } from '../types';
import { PRODUCT_NAMES, cents, esc, priceColour, PRICE_RAMP } from '../format';
import { glossaryTerm } from '../glossary';
import { openStation } from '../components/drawer';

let map: L.Map | null = null;

export function destroyMap(): void {
  if (map) { map.remove(); map = null; }
}

export async function renderMap(root: HTMLElement): Promise<void> {
  root.innerHTML = '<div class="skeleton"></div>';
  const [geo, { sites }] = await Promise.all([loadBoundaries(), loadSites()]);
  let product = prefs.get('mapProduct', 'ULP');
  let layerMode = prefs.get('mapLayer', 'stations');

  const products = ['ULP', 'DSL', 'P95', 'P98', 'E10', 'LPG']
    .filter((p) => sites.filter((s) => s.p[p]).length > 40);
  if (!products.includes(product)) product = products[0];

  root.innerHTML = `
    <div class="view-head">
      <h1>Where fuel is cheap</h1>
      <p>Every station with published coordinates, and the middle price for each
      ${glossaryTerm('sa4', 'ABS region')}. Boundaries are the real ABS Statistical Area Level 4 shapes.</p>
    </div>
    <div class="panel">
      <div class="controls">
        <label for="map-product">Fuel</label>
        <select id="map-product">${products.map((p) => `<option value="${esc(p)}"${p === product ? ' selected' : ''}>${esc(PRODUCT_NAMES[p] ?? p)}</option>`).join('')}</select>
        <div class="seg" id="map-layer" role="group" aria-label="Map layer">
          <button type="button" data-layer="stations">Stations</button>
          <button type="button" data-layer="regions">Regions</button>
        </div>
      </div>
      <div class="map-frame"><div class="map-canvas" id="map-canvas"></div></div>
      <div id="map-legend"></div>
      <p class="brand-sub" style="margin-top:var(--space-sm)" id="map-note"></p>
    </div>
  `;

  const seg = root.querySelector('#map-layer') as HTMLElement;
  const sel = root.querySelector('#map-product') as HTMLSelectElement;
  const syncSeg = () => seg.querySelectorAll('button').forEach((b) => {
    b.setAttribute('aria-pressed', String((b as HTMLElement).dataset.layer === layerMode));
  });
  seg.addEventListener('click', (e) => {
    const btn = (e.target as Element).closest('button') as HTMLElement | null;
    if (!btn) return;
    layerMode = btn.dataset.layer ?? 'stations';
    prefs.set('mapLayer', layerMode);
    syncSeg();
    paint();
  });
  sel.addEventListener('change', () => { product = sel.value; prefs.set('mapProduct', product); paint(); });
  syncSeg();

  const canvas = root.querySelector('#map-canvas') as HTMLElement;
  destroyMap();
  map = L.map(canvas, { minZoom: 3, maxZoom: 13, zoomControl: true, scrollWheelZoom: false });
  map.attributionControl.setPrefix(false);
  L.tileLayer('https://{s}.basemaps.cartocdn.com/light_nolabels/{z}/{x}/{y}{r}.png', {
    attribution: 'Tiles © CARTO · Boundaries: ABS ASGS 2021 (CC BY 4.0) · Prices: FuelWatch WA &amp; Queensland Treasury (CC BY 4.0)',
    subdomains: 'abcd', minZoom: 3, maxZoom: 13,
  }).addTo(map);

  let regionLayer: L.GeoJSON | null = null;
  let stationLayer: L.LayerGroup | null = null;
  let fitted = false;

  function paint(): void {
    if (!map) return;
    if (regionLayer) { regionLayer.remove(); regionLayer = null; }
    if (stationLayer) { stationLayer.remove(); stationLayer = null; }

    const withPrice = sites.filter((s) => s.p[product] && s.lat != null && s.lon != null);
    const prices = withPrice.map((s) => s.p[product]).sort((a, b) => a - b);
    // A 2nd–98th percentile range, so a single 450 c/L roadhouse cannot flatten
    // the whole colour scale into one band.
    const lo = prices[Math.floor(prices.length * 0.02)] ?? 0;
    const hi = prices[Math.floor(prices.length * 0.98)] ?? 1;

    const byRegion = new Map<string, number[]>();
    for (const s of withPrice) {
      let arr = byRegion.get(s.r);
      if (!arr) { arr = []; byRegion.set(s.r, arr); }
      arr.push(s.p[product]);
    }
    const regionMedian = new Map([...byRegion].map(([r, v]) => {
      const sorted = [...v].sort((a, b) => a - b);
      return [r, sorted[Math.floor(sorted.length / 2)]];
    }));

    regionLayer = L.geoJSON(geo as never, {
      style: (f) => {
        const name = (f?.properties as { name: string }).name;
        const med = regionMedian.get(name);
        return {
          fillColor: layerMode === 'regions' && med != null ? priceColour(med, lo, hi) : '#ffffff',
          fillOpacity: layerMode === 'regions' ? (med != null ? 0.78 : 0.25) : 0.12,
          color: '#8a8378',
          weight: 0.8,
        };
      },
      onEachFeature: (f, lyr) => {
        const props = f.properties as { name: string; state: string };
        const med = regionMedian.get(props.name);
        const n = byRegion.get(props.name)?.length ?? 0;
        lyr.bindTooltip(
          `<b>${esc(props.name)}</b><br>${med != null ? `${cents(med)}c middle price · ${n} stations` : 'no stations reporting'}`,
          { sticky: true, className: 'map-tip' },
        );
        lyr.on({
          mouseover: () => (lyr as L.Path).setStyle({ weight: 2.4, color: '#1c1917' }),
          mouseout: () => regionLayer?.resetStyle(lyr as L.Path),
        });
      },
    }).addTo(map);

    if (layerMode === 'stations') {
      stationLayer = L.layerGroup();
      for (const s of withPrice) {
        const price = s.p[product];
        const marker = L.circleMarker([s.lat!, s.lon!], {
          radius: 5,
          fillColor: priceColour(price, lo, hi),
          fillOpacity: 0.88,
          color: '#ffffff',
          weight: 1,
        });
        marker.bindTooltip(
          `<b>${esc(s.n)}</b><br>${esc(s.s)} · ${esc(s.b || 'Independent')}<br>${cents(price)}c ${esc(PRODUCT_NAMES[product] ?? product)}`,
          { direction: 'top', opacity: 0.96, className: 'map-tip' },
        );
        marker.on('mouseover', () => marker.setStyle({ weight: 2.5, radius: 7 }));
        marker.on('mouseout', () => marker.setStyle({ weight: 1, radius: 5 }));
        marker.on('click', () => openStation(s));
        stationLayer.addLayer(marker);
      }
      stationLayer.addTo(map);
    }

    root.querySelector('#map-note')!.textContent = layerMode === 'stations'
      ? `${withPrice.length.toLocaleString()} stations with published coordinates and a recent ${PRODUCT_NAMES[product] ?? product} price. Click any station for its full price list. ${sites.length - withPrice.length} stations report prices but have no coordinates in the source, so they are absent from the map and present everywhere else.`
      : `Middle price per ABS region, from ${withPrice.length.toLocaleString()} stations. Regions with no reporting station are left unshaded rather than coloured as if they were cheap.`;

    renderLegend(root.querySelector('#map-legend') as HTMLElement, lo, hi);

    // Zero-size defence: a fitBounds on a container that has not been laid out
    // fits a 0×0 box and returns the layer's MAXIMUM zoom — rendering a perfect
    // map of one street corner, with no error anywhere.
    if (!fitted && regionLayer) {
      const bounds = regionLayer.getBounds();
      const fit = () => {
        map!.invalidateSize();
        if (bounds.isValid() && canvas.clientHeight > 50) { map!.fitBounds(bounds, { padding: [14, 14] }); fitted = true; }
      };
      const ro = new ResizeObserver(() => { if (canvas.clientHeight > 50) { fit(); ro.disconnect(); } });
      ro.observe(canvas);
      window.setTimeout(fit, 400);
    }
  }
  paint();
}

function renderLegend(host: HTMLElement, lo: number, hi: number): void {
  const steps = PRICE_RAMP.map((colour, i) => {
    const a = lo + ((hi - lo) * i) / PRICE_RAMP.length;
    const b = lo + ((hi - lo) * (i + 1)) / PRICE_RAMP.length;
    return `<span class="key"><span class="swatch" style="background:${colour}"></span>${cents(a, 0)}–${cents(b, 0)}c</span>`;
  }).join('');
  host.innerHTML = `<div class="legend">${steps}<span class="key" style="color:var(--text-tertiary)">cheap → dear, cents per litre</span></div>`;
}

/** Which stations, if any, lack coordinates — surfaced honestly in the note. */
export function ungeocoded(sites: Site[]): number {
  return sites.filter((s) => s.lat == null || s.lon == null).length;
}
